import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { getCustodianAuthorityReview } from '../../api/custodianApi';
import { CustodianQuickActions } from '../../components/dashboard/CustodianQuickActions';
import { KpiCard } from '../../components/ui/KpiCard';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button, BetaTag } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { IconAlert, IconPackage, IconShield } from '../../components/ui/icons';
import { formatDateTime } from '../../utils/format';
import {
  custodianPackageDetailPath,
  displayValue,
} from './custodianReviewLabels';

function filesLabel(row) {
  if (typeof row?.file_count !== 'number') return displayValue(null);
  return `${row.file_count} file${row.file_count === 1 ? '' : 's'}`;
}

async function listEntireAuthorityQueue() {
  const pageSize = 100;
  const items = [];
  let total = 0;

  for (let page = 1; page <= 20; page += 1) {
    const response = await getCustodianAuthorityReview({ page, pageSize });
    const batch = response.items || [];
    total = response.meta?.total ?? items.length + batch.length;
    items.push(...batch);
    if (batch.length < pageSize || items.length >= total) break;
  }

  return { items, total };
}

export function CustodianDashboardPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      setLoading(true);
      setError('');
      try {
        const response = await listEntireAuthorityQueue();
        if (cancelled) return;
        setItems(response.items || []);
        setTotal(response.total || 0);
      } catch (requestError) {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setError(
          requestError.message ||
            'Unable to load Custodian dashboard data.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const withFiles = items.filter((row) => row.file_count > 0).length;
  const missingFiles = items.filter((row) => !(row.file_count > 0));

  const kpis = useMemo(
    () => [
      {
        label: 'Awaiting review',
        value: total,
        subline: 'Routed candidates pending legal authority',
        icon: IconShield,
        accent: true,
      },
      {
        label: 'With source files',
        value: withFiles,
        subline: 'Awaiting-review packages with complete files',
        icon: IconPackage,
      },
      {
        label: 'Missing source files',
        value: missingFiles.length,
        subline: 'Awaiting-review packages with no complete files',
        icon: IconAlert,
      },
    ],
    [missingFiles.length, total, withFiles]
  );

  const columns = useMemo(
    () => [
      {
        key: 'package',
        header: 'Package',
        sortValue: (row) => row.reference_number || row.package_id,
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {displayValue(row.reference_number || row.package_id)}
            </span>
            <span className="block text-[11px] text-eorig-muted">
              {formatDateTime(row.routed_at || row.created_at)}
            </span>
          </div>
        ),
      },
      {
        key: 'program',
        header: 'Program',
        render: (row) =>
          row.program ? (
            <span className="inline-flex max-w-[160px] truncate rounded-md bg-eorig-surface px-2 py-1 text-[11px] text-eorig-muted">
              {row.program}
            </span>
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'originator',
        header: 'Originator',
        render: (row) => displayValue(row.originator),
      },
      {
        key: 'recommendation',
        header: 'Recommendation',
        render: (row) =>
          row.recommendation ? (
            <StatusPill value={row.recommendation} />
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'authority_state',
        header: 'Authority',
        render: (row) => <StatusPill value={row.authority_state} />,
      },
      {
        key: 'file_count',
        header: 'Files',
        render: (row) => (
          <span className="text-eorig-muted">{filesLabel(row)}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BetaTag />
          <p className="text-[13px] text-eorig-muted">
            Legal authority and admission queues — not provider ingestion.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Link to="/custodian/explorer">
            <Button variant="ghost">Custody explorer</Button>
          </Link>
          <Link to="/custodian/awaiting-review">
            <Button variant="gradient">Awaiting review</Button>
          </Link>
        </div>
      </div>

      {error ? (
        <ErrorBanner
          message={error}
          onRetry={() => setReloadToken((value) => value + 1)}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            subline={kpi.subline}
            icon={kpi.icon}
            accent={kpi.accent}
            loading={loading}
          />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
        <div className="lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-eorig-ink">
              Awaiting review
            </h2>
            <Link
              to="/custodian/awaiting-review"
              className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            >
              View all
            </Link>
          </div>
          <DataTable
            columns={columns}
            rows={items.slice(0, 10)}
            rowKey={(row) => row.package_id}
            rowHref={(row) => custodianPackageDetailPath(row.package_id)}
            loading={loading}
            emptyMessage="No packages awaiting Custodian review."
            minWidth={720}
          />
        </div>

        <section
          className="flex flex-col rounded-2xl border border-eorig-border bg-white shadow-eorig-card"
          aria-label="Critical attention"
        >
          <div className="flex items-center justify-between border-b border-eorig-border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                <IconAlert className="h-4 w-4" />
              </span>
              <h2 className="text-sm font-semibold text-eorig-ink">
                Critical attention
              </h2>
            </div>
            <Link
              to="/custodian/awaiting-review"
              className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            >
              View all
            </Link>
          </div>
          <ul className="m-0 list-none space-y-2 p-3">
            {loading ? (
              <li className="px-1 py-6 text-center text-sm text-eorig-muted">
                Loading…
              </li>
            ) : missingFiles.length ? (
              missingFiles.map((row) => (
                <li
                  key={row.package_id}
                  className="rounded-xl border border-eorig-border border-l-[3px] border-l-rose-400 bg-rose-50/40 p-3"
                >
                  <p className="text-sm font-semibold text-eorig-ink">
                    No complete files
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-eorig-muted">
                    {displayValue(row.reference_number || row.package_id)}
                    {row.program ? ` · ${row.program}` : ''}
                  </p>
                  <Link
                    to={custodianPackageDetailPath(row.package_id)}
                    className="mt-2 inline-flex items-center text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
                  >
                    Review →
                  </Link>
                </li>
              ))
            ) : (
              <li className="rounded-xl bg-eorig-surface px-3 py-6 text-center text-sm text-eorig-muted">
                Nothing needs attention right now.
              </li>
            )}
          </ul>
        </section>
      </div>

      <CustodianQuickActions />
    </div>
  );
}
